import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import { useDispatch, useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { MCheckbox } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { Icon } from "@/components-v2/Icon";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MSelect } from "@/components-v2/Select";
import { Sortable, SortableList } from "@/components-v2/SortableList";
import { MTextInput } from "@/components-v2/TextInput";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { FieldRef, personFields } from "./dashboardApiClient";
import { dialogPreviewKey, draftCardKey } from "./dashboardPreviewDomain";
import {
  PreviewCard,
  PreviewAsSelect,
  PreviewHeadingBar,
  PreviewTabBar,
  PreviewText,
  PreviewVisibilityNote,
  usePreviewData,
} from "./dashboardPreviewComponents";
import { StringFieldModel } from "@/common/commonForms";
import { DCEvent } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import {
  CustomFieldDefinition,
  systemFieldKeys,
  systemFieldLabels,
} from "@/customFields/customFieldsDomain";
import {
  addDashboardCard,
  addEntityField,
  addVisibleGroup,
  cancelDelete,
  canSaveCard,
  removePersonField,
  removeCardRole,
  personFieldLabel,
  movePersonField,
  moveCardRole,
  cardRoleOptions,
  cardRoleLabel,
  cardRoleId,
  addPersonField,
  addCardRole,
  canSaveDashboard,
  cardConfig,
  CardForm,
  cardTypeLabelFor,
  closeDialog,
  confirmDelete,
  DashboardForm,
  DeleteDialog,
  deleteMessage,
  editorTexts,
  fieldRefId,
  moveDashboardCard,
  moveEntityField,
  openDeleteDialog,
  openMapTemplates,
  removeDashboardCard,
  removeEntityField,
  removeVisibleGroup,
  saveCard,
  saveDashboard,
  textFor,
} from "./dashboardDialogsDomain";
import { dashboardGroupLabel, DashboardSettingsState } from "./dashboardSettingsDomain";

export const DashboardSettingsDialogView: FC<{ state: DashboardSettingsState }> = observer(
  ({ state }) => {
    switch (state.dialog?.type) {
      case "dashboard":
        return <DashboardDialog state={state} form={state.dialog} />;
      case "card":
        return <CardDialog state={state} form={state.dialog} />;
      case "delete":
        return <DeleteConfirmation state={state} dialog={state.dialog} />;
      case undefined:
        return null;
    }
  },
);

const EditorDialog: FC<{
  state: DashboardSettingsState;
  preview: ReactNode;
  settings: ReactNode;
  footer: ReactNode;
}> = observer(({ state, preview, settings, footer }) => (
  <Dialog maxWidth={"xl"} height={"full"} onCancel={closeDialog(state)} actions={footer}>
    <Box gap={"lg"} grow alignItems={"stretch"}>
      <VBox grow bg={"base"} padding={"lg"} gap={"lg"} rounded>
        {preview}
      </VBox>
      <VBox gap={"lg"} flex={"none"} className={"w-[28rem] self-start"}>
        {settings}
      </VBox>
    </Box>
  </Dialog>
));

const Footer: FC<{
  state: DashboardSettingsState;
  onDelete?: () => void;
  deleteLabel:
    | "enterprise-account.dashboards.delete-dashboard"
    | "enterprise-account.dashboards.delete-card";
  onSave: () => void;
  canSave: boolean;
  isSubmitting: boolean;
}> = observer(({ state, onDelete, deleteLabel, onSave, canSave, isSubmitting }) => {
  const dispatch = useDispatch();
  return (
    <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"}>
      <Box>
        {onDelete && <LinkButton color={"error"} label={{ key: deleteLabel }} onClick={onDelete} />}
      </Box>
      <Box gap={"md"}>
        <Button
          variant={"secondary"}
          label={{ key: "cancel" }}
          onClick={() => dispatch(closeDialog(state))}
        />
        <Button
          variant={"primary"}
          label={{ key: "save" }}
          loading={isSubmitting}
          disabled={!canSave || isSubmitting}
          onClick={onSave}
        />
      </Box>
    </Box>
  );
});

const DashboardDialog: FC<{ state: DashboardSettingsState; form: DashboardForm }> = observer(
  ({ state, form }) => {
    const { t } = useTranslation();
    const language = useLanguage();
    const dispatch = useDispatch();
    const overview = state.overview;
    const cards = form.cardIds.flatMap(
      (id) => overview?.cards.find((card) => card.id === id) ?? [],
    );
    const groups = form.visibleTo.flatMap(
      (grantee) => overview?.groups.find((group) => group.grantee === grantee) ?? [],
    );
    const addableGroups =
      overview?.groups.filter((group) => !form.visibleTo.includes(group.grantee)) ?? [];
    const addableCards = overview?.cards.filter((card) => !form.cardIds.includes(card.id)) ?? [];
    const dashboard = form.dashboard;
    const heading = form.showHeading.value
      ? textFor(editorTexts(form.heading), language)
      : undefined;
    const hasDatePicker = form.hasDatePicker.value === true;
    usePreviewData(
      state,
      cards.map((card) => ({ key: dialogPreviewKey(card.id), config: card.config })),
      hasDatePicker,
      dashboard?.visibleTo,
    );
    return (
      <EditorDialog
        state={state}
        preview={
          <>
            <PreviewTabBar
              title={
                <PreviewText
                  text={textFor(editorTexts(form.title), language)}
                  placeholder={"enterprise-account.dashboards.dashboard-title"}
                />
              }
              end={<PreviewAsSelect state={state} savedVisibleTo={dashboard?.visibleTo} />}
            />
            <PreviewVisibilityNote
              state={state}
              savedVisibleTo={dashboard?.visibleTo}
              visibleTo={form.visibleTo}
              isEnabled={form.status.value === "ENABLED"}
            />
            <PreviewHeadingBar
              state={state}
              dateInputId={"dashboardDialogAsOf"}
              heading={heading}
              hasDatePicker={hasDatePicker}
            />
            {cards.length === 0 ? (
              <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-cards")}</Box>
            ) : (
              <Grid cols={1} colsLg={2} gap={"lg"}>
                {cards.map((card) => (
                  <PreviewCard
                    key={card.id}
                    state={state}
                    heading={card.heading}
                    previewKey={dialogPreviewKey(card.id)}
                    config={card.config}
                  />
                ))}
              </Grid>
            )}
          </>
        }
        settings={
          <>
            <h4>
              {t(
                dashboard
                  ? "enterprise-account.dashboards.edit-dashboard"
                  : "enterprise-account.dashboards.new-dashboard",
              )}
            </h4>
            <MTextInput
              model={form.internalName}
              label={{ key: "enterprise-account.dashboards.internal-name" }}
              message={t("enterprise-account.dashboards.internal-name.help")}
            />
            <LocalizedTextEditor model={form.title} inputType={"string"} />
            <VBox gap={"sm"}>
              <b>{t("enterprise-account.dashboards.publish-status")}</b>
              <MRadioButtonGroup model={form.status} horizontal />
            </VBox>
            <VBox gap={"sm"}>
              <b>{t("enterprise-account.dashboards.column.visible-to")}</b>
              {groups.map((group) => (
                <ListRow
                  key={group.grantee}
                  icon={"users"}
                  label={t(dashboardGroupLabel(group))}
                  caption={t({
                    key: "enterprise-account.dashboards.member-count",
                    params: { count: group.memberCount },
                  })}
                  onRemove={() => dispatch(removeVisibleGroup(state, group.grantee))}
                />
              ))}
              <Box>
                <DropdownMenu
                  button={{
                    type: "link",
                    icon: "plus",
                    label: { key: "enterprise-account.dashboards.add-user-group" },
                  }}
                  disabled={addableGroups.length === 0}
                  alignment={"start"}
                  items={addableGroups.map((group) => ({
                    type: "item",
                    label: dashboardGroupLabel(group),
                    categoryTag: {
                      key: "enterprise-account.dashboards.member-count",
                      params: { count: group.memberCount },
                    },
                    onSelect: addVisibleGroup(state, group.grantee),
                  }))}
                />
              </Box>
            </VBox>
            <VBox gap={"sm"}>
              <b>{t("enterprise-account.dashboards.design")}</b>
              <MCheckbox
                model={form.showHeading}
                label={{ key: "enterprise-account.dashboards.page-heading" }}
              />
              <VBox gap={"sm"} paddingLeft={"lg"} alignItems={"stretch"}>
                {form.showHeading.value && (
                  <LocalizedTextEditor model={form.heading} inputType={"string"} hideLabel />
                )}
                <MCheckbox
                  model={form.hasDatePicker}
                  label={{ key: "enterprise-account.dashboards.date-picker" }}
                />
              </VBox>
            </VBox>
            <VBox gap={"sm"}>
              <b>{t("enterprise-account.dashboards.tab.cards")}</b>
              <SortableList
                items={cards}
                onMove={(card, toCard) => dispatch(moveDashboardCard(state, card.id, toCard.id))}
              >
                {cards.map((card) => (
                  <Sortable key={card.id} id={card.id}>
                    {(dragProps) => (
                      <ListRow
                        dragHandle={
                          <Box
                            fg={"subtle"}
                            htmlProps={{ ...dragProps.dragAttributes, ...dragProps.dragListeners }}
                          >
                            <Icon name={"dots-six-vertical"} />
                          </Box>
                        }
                        label={card.internalName}
                        caption={t(cardTypeLabelFor(card.type))}
                        onRemove={() => dispatch(removeDashboardCard(state, card.id))}
                      />
                    )}
                  </Sortable>
                ))}
              </SortableList>
              <Box>
                <DropdownMenu
                  button={{
                    type: "link",
                    icon: "plus",
                    label: { key: "enterprise-account.dashboards.add-card" },
                  }}
                  disabled={addableCards.length === 0}
                  alignment={"start"}
                  items={addableCards.map((card) => ({
                    type: "item",
                    label: card.internalName,
                    categoryTag: cardTypeLabelFor(card.type),
                    onSelect: addDashboardCard(state, card.id),
                  }))}
                />
              </Box>
            </VBox>
          </>
        }
        footer={
          <Footer
            state={state}
            deleteLabel={"enterprise-account.dashboards.delete-dashboard"}
            onDelete={
              dashboard
                ? () => dispatch(openDeleteDialog(state, { kind: "dashboard", dashboard }, form))
                : undefined
            }
            onSave={() => dispatch(saveDashboard(state, form))}
            canSave={canSaveDashboard(form)}
            isSubmitting={form.isSubmitting}
          />
        }
      />
    );
  },
);

const ListRow: FC<{
  icon?: "users";
  dragHandle?: ReactNode;
  label: string;
  caption?: string;
  onRemove: () => void;
}> = ({ icon, dragHandle, label, caption, onRemove }) => (
  <Box
    border
    rounded
    alignItems={"center"}
    gap={"sm"}
    paddingX={"md"}
    paddingY={"sm"}
    bg={"default"}
  >
    {dragHandle}
    {icon && <Icon name={icon} />}
    <Box grow>{label}</Box>
    {caption && (
      <Box fg={"subtle"} textSize={"sm"}>
        {caption}
      </Box>
    )}
    <IconButton icon={"x"} tooltip={{ key: "remove" }} color={"gray"} onClick={onRemove} />
  </Box>
);

const CardDialog: FC<{ state: DashboardSettingsState; form: CardForm }> = observer(
  ({ state, form }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const card = form.card;
    const config = cardConfig(form);
    usePreviewData(state, [{ key: draftCardKey, config }], true);
    return (
      <EditorDialog
        state={state}
        preview={
          <>
            <PreviewTabBar end={<PreviewAsSelect state={state} />} />
            <PreviewHeadingBar state={state} dateInputId={"cardDialogAsOf"} hasDatePicker />
            <Box justifyContent={"center"} paddingTop={"xl"}>
              <VBox className={"w-[24rem]"}>
                <PreviewCard
                  state={state}
                  heading={editorTexts(form.heading)}
                  previewKey={draftCardKey}
                  config={config}
                />
              </VBox>
            </Box>
          </>
        }
        settings={
          <>
            <h4>
              {t(
                card
                  ? "enterprise-account.dashboards.edit-card"
                  : "enterprise-account.dashboards.new-card",
              )}
            </h4>
            <MTextInput
              model={form.internalName}
              label={{ key: "enterprise-account.dashboards.internal-name" }}
              message={t("enterprise-account.dashboards.internal-name.help")}
            />
            <LocalizedTextEditor model={form.heading} inputType={"string"} />
            <MSelect model={form.cardType} label={{ key: "type" }} fullWidth />
            {form.cardType.value === "ENTITY_DATA" && (
              <EntityFieldsEditor state={state} form={form} />
            )}
            {form.cardType.value === "COMPANY_ROLES" && (
              <CardRolesEditor state={state} form={form} />
            )}
            {form.cardType.value === "TEMPLATE" && <MapCardEditor state={state} form={form} />}
            {form.cardType.value === "ROLE_HOLDER" && (
              <>
                <MSelect model={form.role} label={{ key: "role" }} fullWidth />
                <PersonFieldsEditor state={state} form={form} />
              </>
            )}
          </>
        }
        footer={
          <Footer
            state={state}
            deleteLabel={"enterprise-account.dashboards.delete-card"}
            onDelete={
              card
                ? () => dispatch(openDeleteDialog(state, { kind: "card", card }, form))
                : undefined
            }
            onSave={() => dispatch(saveCard(state, form))}
            canSave={canSaveCard(form)}
            isSubmitting={form.isSubmitting}
          />
        }
      />
    );
  },
);

const DeleteConfirmation: FC<{ state: DashboardSettingsState; dialog: DeleteDialog }> = observer(
  ({ state, dialog }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={{
          key:
            dialog.target.kind === "dashboard"
              ? "enterprise-account.dashboards.delete-dashboard"
              : "enterprise-account.dashboards.delete-card",
        }}
        maxWidth={"sm"}
        onCancel={cancelDelete(state)}
        onSubmit={confirmDelete(state, dialog)}
        submitLabel={{ key: "settings.delete" }}
        submitColor={"error"}
        isSubmitting={dialog.isSubmitting}
      >
        <div>{t(deleteMessage(dialog))}</div>
      </Dialog>
    );
  },
);

type ListEditorItem = { id: string; label: string };

type ListEditorOption = { id: string; label: string; tag?: TMessage; onSelect: DCEvent[] };

const ListEditor: FC<{
  title: TMessage;
  addLabel: TMessage;
  items: ListEditorItem[];
  options: ListEditorOption[];
  filter: StringFieldModel;
  onMove: (id: string, toId: string) => void;
  onRemove: (id: string) => void;
}> = observer(({ title, addLabel, items, options, filter, onMove, onRemove }) => {
  const { t } = useTranslation();
  const query = filter.inputValue.trim().toLowerCase();
  return (
    <VBox gap={"sm"}>
      <b>{t(title)}</b>
      <SortableList items={items} onMove={(item, toItem) => onMove(item.id, toItem.id)}>
        {items.map((item) => (
          <Sortable key={item.id} id={item.id}>
            {(dragProps) => (
              <ListRow
                dragHandle={
                  <Box
                    fg={"subtle"}
                    htmlProps={{ ...dragProps.dragAttributes, ...dragProps.dragListeners }}
                  >
                    <Icon name={"dots-six-vertical"} />
                  </Box>
                }
                label={item.label}
                onRemove={() => onRemove(item.id)}
              />
            )}
          </Sortable>
        ))}
      </SortableList>
      <Box>
        <DropdownMenu
          button={{ type: "link", icon: "plus", label: addLabel }}
          alignment={"start"}
          filter={filter.inputValue}
          filterPlaceholder={{ key: "search" }}
          onFilterChange={filter.setInputValue}
          disabled={options.length === 0}
          items={options
            .filter((option) => query === "" || option.label.toLowerCase().includes(query))
            .map((option) => ({
              type: "item",
              label: option.label,
              categoryTag: option.tag,
              onSelect: option.onSelect,
            }))}
        />
      </Box>
    </VBox>
  );
});

const customFieldTag: TMessage = { key: "enterprise-account.dashboards.custom-field" };

const FieldsEditor = <K extends string>({
  definitions,
  objectType,
  fields,
  systemFields,
  systemLabel,
  filter,
  onAdd,
  onMove,
  onRemove,
}: {
  definitions: CustomFieldDefinition[];
  objectType: CustomFieldDefinition["objectType"];
  fields: FieldRef<K>[];
  systemFields: readonly K[];
  systemLabel: (field: K) => TMessage;
  filter: StringFieldModel;
  onAdd: (field: FieldRef<K>) => DCEvent[];
  onMove: (id: string, toId: string) => void;
  onRemove: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const label = (field: FieldRef<K>): string =>
    field.source === "SYSTEM"
      ? t(systemLabel(field.field))
      : (definitions.find((definition) => definition.id === field.definitionId)?.name ??
        t("enterprise-account.dashboards.unknown-field"));
  const chosen = new Set(fields.map(fieldRefId));
  const options: FieldRef<K>[] = [
    ...systemFields.map((field): FieldRef<K> => ({ source: "SYSTEM", field })),
    ...definitions
      .filter((definition) => definition.objectType === objectType && !definition.archivedAt)
      .map((definition): FieldRef<K> => ({ source: "CUSTOM", definitionId: definition.id })),
  ];
  return (
    <ListEditor
      title={{ key: "enterprise-account.fields" }}
      addLabel={{ key: "entity-data.add-field" }}
      items={fields.map((field) => ({ id: fieldRefId(field), label: label(field) }))}
      options={options
        .filter((field) => !chosen.has(fieldRefId(field)))
        .map((field) => ({
          id: fieldRefId(field),
          label: label(field),
          tag: field.source === "CUSTOM" ? customFieldTag : undefined,
          onSelect: onAdd(field),
        }))}
      filter={filter}
      onMove={onMove}
      onRemove={onRemove}
    />
  );
};

const MapCardEditor: FC<{ state: DashboardSettingsState; form: CardForm }> = observer(
  ({ state, form }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    return (
      <VBox gap={"sm"}>
        <Box justifyContent={"space-between"} alignItems={"center"}>
          <b>{t("ownership.map")}</b>
          <LinkButton
            label={{ key: "enterprise-account.dashboards.manage-maps" }}
            onClick={() => dispatch(openMapTemplates(state))}
          />
        </Box>
        <MSelect model={form.template} placeholder={t("choose")} fullWidth />
      </VBox>
    );
  },
);

const EntityFieldsEditor: FC<{ state: DashboardSettingsState; form: CardForm }> = observer(
  ({ state, form }) => {
    const dispatch = useDispatch();
    return (
      <FieldsEditor
        definitions={state.definitions}
        objectType={"COMPANY"}
        fields={form.entityFields}
        systemFields={systemFieldKeys}
        systemLabel={(field) => systemFieldLabels[field]}
        filter={form.fieldFilter}
        onAdd={(field) => addEntityField(state, field)}
        onMove={(id, toId) => dispatch(moveEntityField(state, id, toId))}
        onRemove={(id) => dispatch(removeEntityField(state, id))}
      />
    );
  },
);

const CardRolesEditor: FC<{ state: DashboardSettingsState; form: CardForm }> = observer(
  ({ state, form }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const chosen = new Set(form.roles.map(cardRoleId));
    return (
      <ListEditor
        title={{ key: "roles" }}
        addLabel={{ key: "enterprise-account.company-roles.add" }}
        items={form.roles.map((role) => ({
          id: cardRoleId(role),
          label: t(cardRoleLabel(role, state.roleTypes)),
        }))}
        options={cardRoleOptions(state.roleTypes)
          .filter((role) => !chosen.has(cardRoleId(role)))
          .map((role) => ({
            id: cardRoleId(role),
            label: t(cardRoleLabel(role, state.roleTypes)),
            onSelect: addCardRole(state, role),
          }))}
        filter={form.fieldFilter}
        onMove={(id, toId) => dispatch(moveCardRole(state, id, toId))}
        onRemove={(id) => dispatch(removeCardRole(state, id))}
      />
    );
  },
);

const PersonFieldsEditor: FC<{ state: DashboardSettingsState; form: CardForm }> = observer(
  ({ state, form }) => {
    const dispatch = useDispatch();
    return (
      <FieldsEditor
        definitions={state.definitions}
        objectType={"COMPANY_ROLE"}
        fields={form.personFields}
        systemFields={personFields}
        systemLabel={personFieldLabel}
        filter={form.fieldFilter}
        onAdd={(field) => addPersonField(state, field)}
        onMove={(id, toId) => dispatch(movePersonField(state, id, toId))}
        onRemove={(id) => dispatch(removePersonField(state, id))}
      />
    );
  },
);
